/**
 * What the page holds, as the landing audit (`redline-landing-reconciliation`)
 * needs it after an apply: for each bookmark a card names, the paragraph's
 * accepted text, the accepted text of the paragraphs right after it, the comments
 * anchored on it or on the paragraph after it, the table row it sits in, and the
 * tables right beside it.
 *
 * Every audit, a single Apply's, the conflict resolver's and a bulk run's end, reads
 * the page the same way: one `body.getOoxml()`, one Word round trip whatever the
 * number of cards, and every fact read off that markup through `OoxmlRange`, the
 * reader the builder writes with. Every bookmark resolves to its own paragraph in the
 * body's markup, a table cell to its own cell (no row comes back in its place), and
 * the paragraphs around it, the comments on it and the tables beside it are read off
 * the same markup, so the audit and the write agree on what a paragraph says.
 */
import { DocumentComments } from "@common/utils/redline/ooxml/document-comments";
import { OoxmlNumbering } from "@common/utils/redline/ooxml/ooxml-numbering";
import { OoxmlOutlineLevels } from "@common/utils/redline/ooxml/ooxml-outline-levels";
import { OoxmlRange } from "@common/utils/redline/ooxml-range";
import { WordTokenizer } from "@common/utils/redline/text/word-tokenizer";
import type { DocumentParagraph } from "@common/utils/redline/wire/document-paragraph";
import type { DocumentTableCell } from "@common/utils/redline/wire/table-cell-path";

import { officeWordDocument } from "./anchor/anchor-tracker-registry";
import { withWordContextRead } from "./document";

/**
 * What the audit asks the page about one bookmark.
 *
 *   - followingParagraphCount -> how many paragraphs after the bookmarked one to read, for new
 *                                paragraphs a card inserted there; 0 reads none
 *   - nearbyParagraphCount    -> how many paragraphs on each side of the bookmarked one to read,
 *                                for a card that edits its words: where the card's text stands
 *                                when the apply wrote it as a paragraph of its own; 0 reads none
 *   - readsComments           -> read the comments on the paragraph and on the one after it
 *   - readsTables             -> read the table row the paragraph sits in and the tables beside it
 */
export interface LandingPageRequest {
    bookmark: string;
    followingParagraphCount: number;
    nearbyParagraphCount: number;
    readsComments: boolean;
    readsTables: boolean;
}

/**
 * The table the bookmarked paragraph sits in: which row it is, and every row's
 * cell texts, one string per cell as Word's `Table.values` lists them, which hold
 * tracked insertions.
 */
export interface EnclosingTableFacts {
    anchorRowIndex: number;
    rowCellTexts: string[][];
}

/**
 * What the page holds around one bookmark. `writtenNearbyAcceptedTexts` are the
 * accepted texts of the paragraphs within `nearbyParagraphCount` on either side that
 * hold no word once every change is rejected: paragraphs an apply wrote.
 *
 * EMPTY lists when the request did not ask for them.
 */
export interface BookmarkedParagraphFacts {
    acceptedText: string;
    /**
     * The accepted texts of every paragraph the bookmark spans, joined by a line break:
     * `acceptedText` alone unless the bookmark runs on past its first paragraph.
     *
     * WHY
     *   A user who splits a bookmarked paragraph by pressing Enter leaves the bookmark's
     *   end in the split-off half (R22, 2026-10-16: "7.4 … in full." | "Supplier retains
     *   …"). A card proposed before the split may have changed words of either half, and
     *   only this text holds both.
     */
    spannedAcceptedText: string;
    followingAcceptedTexts: string[];
    writtenNearbyAcceptedTexts: string[];
    commentTexts: string[];
    enclosingTable: EnclosingTableFacts | null;
    nearbyTablesCellTexts: string[][][];
}

/**
 * The page's answers by bookmark.
 *
 * WHEN a bookmark is not in the map
 *   CONSEQUENCE
 *     it is gone from the document: the paragraph was deleted or cut.
 */
export type LandingPageFacts = Map<string, BookmarkedParagraphFacts>;

/** Reads the page for the given requests; the audit takes it as a parameter so tests hand it a fake page. */
export type LandingPageReader = (requests: LandingPageRequest[]) => Promise<LandingPageFacts>;

/** True when a body paragraph has no word once every tracked change is rejected: a paragraph an apply wrote, or an empty one. */
const holdsNoWordOnceRejected = (bodyParagraph: DocumentParagraph): boolean => WordTokenizer.words(bodyParagraph.allChangesRejectedText).length === 0;

/** The cell a body paragraph is written in, the last entry of its table path; null for a paragraph outside every table. */
const innermostCellOf = (bodyParagraph: DocumentParagraph): DocumentTableCell | null => bodyParagraph.tableCellPath.at(-1) ?? null;

/**
 * The paragraphs after the bookmarked ones: `followingParagraphCount` of them, and
 * while the last one read is content an apply wrote, that many more.
 *
 * WHY
 *   Several cards can insert after the same paragraph. Another card's new paragraphs,
 *   or every cell paragraph of its new table, can stand between the anchor and this
 *   card's insertion, past the count the audit asked for; the card was written but
 *   would read as not applied. So the walk goes on, a batch of that count at a time,
 *   until a paragraph the document held before the apply or the body's end.
 *
 * EXAMPLE
 *   followingParagraphCount 3, the 3 after the anchor are cells of a new table
 *   -> 3 more are read, and so on, until a paragraph the document held before is read
 *
 * EDGE CASE
 *   The body's end stops the walk wherever it falls.
 */
function followingParagraphsOf(bodyParagraphs: DocumentParagraph[], lastBookmarkedIndex: number, followingParagraphCount: number): DocumentParagraph[] {
    if (followingParagraphCount === 0) return [];
    const followingParagraphs: DocumentParagraph[] = [];
    /** The index of the last paragraph of the current batch: the bookmarked paragraph's index plus the count asked for. */
    let batchEndIndex = lastBookmarkedIndex + followingParagraphCount;
    for (let paragraphIndex = lastBookmarkedIndex + 1; paragraphIndex < bodyParagraphs.length && paragraphIndex <= batchEndIndex; paragraphIndex += 1) {
        followingParagraphs.push(bodyParagraphs[paragraphIndex]);
        /** A batch that ends on a paragraph an apply wrote is followed by another batch of the same size. */
        if (paragraphIndex === batchEndIndex && holdsNoWordOnceRejected(bodyParagraphs[paragraphIndex])) batchEndIndex += followingParagraphCount;
    }
    return followingParagraphs;
}

/**
 * The accepted texts of the paragraphs within `nearbyParagraphCount` before and
 * after the bookmarked ones that an apply wrote: the ones that hold no word once
 * every tracked change is rejected.
 *
 * WHY only written paragraphs
 *   A clause the user deleted as a tracked change keeps the card's bookmark and reads
 *   empty, and the apply writes the card's text as a new paragraph beside it (6.2:
 *   "…meet quarterly…" struck, "…meet monthly…" inserted after it). The card is judged
 *   by that new paragraph; a neighbour the document held before, which may read just
 *   like the card (a repeated clause), is never taken for it.
 *
 * EXAMPLE
 *   bookmarked index 40, count 3 -> indexes 37, 38, 39 and 41, 42, 43 are looked at;
 *   only those holding no word once rejected, and some once accepted, are kept
 */
function writtenNearbyTextsOf(bodyParagraphs: DocumentParagraph[], firstBookmarkedIndex: number, lastBookmarkedIndex: number, nearbyParagraphCount: number): string[] {
    /** `Math.max(0, …)` keeps the walk back inside the body when the bookmark sits within the count of its start. */
    const paragraphsBefore = bodyParagraphs.slice(Math.max(0, firstBookmarkedIndex - nearbyParagraphCount), firstBookmarkedIndex);
    /** `+ 1` starts after the last bookmarked paragraph; `slice` stops at the body's end on its own. */
    const paragraphsAfter = bodyParagraphs.slice(lastBookmarkedIndex + 1, lastBookmarkedIndex + 1 + nearbyParagraphCount);
    return [...paragraphsBefore, ...paragraphsAfter]
        .filter(holdsNoWordOnceRejected)
        .map((bodyParagraph) => bodyParagraph.allChangesAcceptedText)
        .filter((acceptedText) => WordTokenizer.words(acceptedText).length > 0);
}

/**
 * Every row of one table as its cells' accepted texts, the shape Word's
 * `Table.values` gives: one string per cell, a cell's paragraphs joined by a line
 * break. Only paragraphs written directly in the table's cells count; a table
 * nested in a cell has its own number and its own rows.
 *
 * EXAMPLE
 *   table 3 holding  Item | Fee  /  Platform | $125,000
 *   -> [["Item", "Fee"], ["Platform", "$125,000"]]
 */
function tableCellTextsOf(bodyParagraphs: DocumentParagraph[], tableNumber: number): string[][] {
    const directCellParagraphs = bodyParagraphs
        .map((bodyParagraph) => ({ bodyParagraph, cell: innermostCellOf(bodyParagraph) }))
        .filter((placed): placed is { bodyParagraph: DocumentParagraph; cell: DocumentTableCell } => placed.cell !== null && placed.cell.tableNumber === tableNumber);
    if (directCellParagraphs.length === 0) return [];
    const rowCellTexts: string[][] = Array.from({ length: directCellParagraphs[0].cell.rowCount }, () => []);
    for (const { bodyParagraph, cell } of directCellParagraphs) {
        /** `rowNumber` and `cellNumber` are 1-based; `- 1` turns them into the array positions (row 1 cell 2 -> [0][1]). */
        const rowCells = rowCellTexts[cell.rowNumber - 1];
        const cellPosition = cell.cellNumber - 1;
        rowCells[cellPosition] = rowCells[cellPosition] === undefined ? bodyParagraph.allChangesAcceptedText : `${rowCells[cellPosition]}\n${bodyParagraph.allChangesAcceptedText}`;
    }
    /** A position no paragraph filled (a hole left by a merged cell) reads as an empty string, as `Table.values` lists it. */
    return rowCellTexts.map((rowCells) => Array.from({ length: rowCells.length }, (_, cellPosition) => rowCells[cellPosition] ?? ""));
}

/**
 * The table the bookmarked paragraph is written in, with the row it sits on.
 *
 * NULL for a paragraph outside every table.
 *
 * EXAMPLE
 *   the Fee cell of row 2 -> { anchorRowIndex: 1, rowCellTexts: every row of that table }
 */
function enclosingTableOf(bodyParagraphs: DocumentParagraph[], bookmarkedIndex: number): EnclosingTableFacts | null {
    const anchorCell = innermostCellOf(bodyParagraphs[bookmarkedIndex]);
    if (anchorCell === null) return null;
    /** `rowNumber` is 1-based and `anchorRowIndex` 0-based, as Word's `TableCell.rowIndex` is: row 2 -> 1. */
    return { anchorRowIndex: anchorCell.rowNumber - 1, rowCellTexts: tableCellTextsOf(bodyParagraphs, anchorCell.tableNumber) };
}

/** The index of the first paragraph after every paragraph of `tableNumber`; the body's length when the table ends the body. */
function indexAfterTable(bodyParagraphs: DocumentParagraph[], tableNumber: number): number {
    let lastIndexInTable = -1;
    bodyParagraphs.forEach((bodyParagraph, paragraphIndex) => {
        if (bodyParagraph.tableCellPath.some((cell) => cell.tableNumber === tableNumber)) lastIndexInTable = paragraphIndex;
    });
    /** `+ 1` is the paragraph right after the table's last paragraph. */
    return lastIndexInTable + 1;
}

/**
 * The tables a table a card inserted can stand in, by number (`TableChangeWriter`
 * writes a new table in each of these places).
 *
 * CASES
 *   - a table nested in the anchor's cell                 -> its number
 *   - the paragraph right after the bookmarked ones        -> the table it is written in
 *   - the paragraph right after the anchor's table, and
 *     after its outer table when the anchor sits nested     -> the table each is written in
 *   - every following paragraph read                       -> the table each is written in
 */
function nearbyTableNumbersOf(bodyParagraphs: DocumentParagraph[], firstBookmarkedIndex: number, lastBookmarkedIndex: number, followingParagraphs: DocumentParagraph[]): number[] {
    const anchorPath = bodyParagraphs[firstBookmarkedIndex].tableCellPath;
    const isInAnchorCell = (cell: DocumentTableCell, depth: number): boolean =>
        cell.tableNumber === anchorPath[depth].tableNumber && cell.rowNumber === anchorPath[depth].rowNumber && cell.cellNumber === anchorPath[depth].cellNumber;
    const nestedTableNumbers = anchorPath.length === 0
        ? []
        : bodyParagraphs
            .filter((bodyParagraph) => bodyParagraph.tableCellPath.length > anchorPath.length && anchorPath.every((_, depth) => isInAnchorCell(bodyParagraph.tableCellPath[depth], depth)))
            .map((bodyParagraph) => bodyParagraph.tableCellPath[anchorPath.length].tableNumber);
    /** The anchor's own table is the last entry of its path, its outer table the one before; a body paragraph has neither. */
    const anchorTableNumbers = anchorPath.slice(-2).map((cell) => cell.tableNumber);
    const paragraphsBeside = [lastBookmarkedIndex + 1, ...anchorTableNumbers.map((tableNumber) => indexAfterTable(bodyParagraphs, tableNumber))]
        .filter((paragraphIndex) => paragraphIndex < bodyParagraphs.length)
        .map((paragraphIndex) => bodyParagraphs[paragraphIndex]);
    const besideTableNumbers = [...paragraphsBeside, ...followingParagraphs]
        .map(innermostCellOf)
        .filter((cell): cell is DocumentTableCell => cell !== null)
        .map((cell) => cell.tableNumber);
    return Array.from(new Set([...nestedTableNumbers, ...besideTableNumbers]));
}

/**
 * The index of the last body paragraph listing `bookmark`, from `firstIndex` on: a
 * bookmark spanning paragraphs is listed on each, and the paragraphs after it start
 * after the last one.
 *
 * EXAMPLE
 *   bookmark listed on paragraphs 12 and 13 -> 13
 */
function lastIndexNaming(bodyParagraphs: DocumentParagraph[], bookmark: string, firstIndex: number): number {
    let lastIndex = firstIndex;
    for (let paragraphIndex = firstIndex + 1; paragraphIndex < bodyParagraphs.length; paragraphIndex += 1) {
        if (bodyParagraphs[paragraphIndex].bookmarkNames.includes(bookmark)) lastIndex = paragraphIndex;
    }
    return lastIndex;
}

/**
 * The landing facts of every request, read off the whole body's markup.
 *
 * RETURNS
 *   one entry per bookmark the body still names; a bookmark that is gone has no
 *   entry, which the audit reads as the paragraph gone.
 *
 * EXAMPLE
 *   request { bookmark _fee, nearbyParagraphCount 3 }, the Fee cell of row 2 reading
 *   <del>$120,000</del><ins>$125,000</ins> -> acceptedText "$125,000"
 */
export function landingPageFactsOfBody(bodyOoxml: string, requests: LandingPageRequest[]): LandingPageFacts {
    const bodyRange = new OoxmlRange(bodyOoxml);
    const bodyParagraphs = bodyRange.documentParagraphs(DocumentComments.fromPackage(bodyOoxml), OoxmlNumbering.none(), OoxmlOutlineLevels.none());
    const paragraphSpansByBookmark = bodyRange.bookmarkParagraphSpans(requests.map((request) => request.bookmark));
    const facts: LandingPageFacts = new Map();
    for (const request of requests) {
        const firstBookmarkedIndex = bodyParagraphs.findIndex((bodyParagraph) => bodyParagraph.bookmarkNames.includes(request.bookmark));
        if (firstBookmarkedIndex === -1) continue;
        const lastBookmarkedIndex = lastIndexNaming(bodyParagraphs, request.bookmark, firstBookmarkedIndex);
        /**
         * The last paragraph the bookmark touches: the one its end is in when that sits past
         * the last paragraph listing it (`OoxmlRange.bookmarkParagraphSpans`).
         *
         * EXAMPLE
         *   7.4 split by hand: listed on 176, end in 177 -> 177
         */
        const lastSpannedIndex = Math.max(lastBookmarkedIndex, paragraphSpansByBookmark.get(request.bookmark)?.lastParagraphIndex ?? lastBookmarkedIndex);
        const followingParagraphs = followingParagraphsOf(bodyParagraphs, lastBookmarkedIndex, request.followingParagraphCount);
        facts.set(request.bookmark, {
            acceptedText: bodyParagraphs[firstBookmarkedIndex].allChangesAcceptedText,
            /**
             * `lastSpannedIndex + 1` because the slice is half-open and the last paragraph the
             * bookmark touches belongs to it.
             *
             * EXAMPLE
             *   bookmark from paragraph 176 to 177 -> slice(176, 178) -> both halves of 7.4
             */
            spannedAcceptedText: bodyParagraphs.slice(firstBookmarkedIndex, lastSpannedIndex + 1).map((bodyParagraph) => bodyParagraph.allChangesAcceptedText).join("\n"),
            followingAcceptedTexts: followingParagraphs.map((bodyParagraph) => bodyParagraph.allChangesAcceptedText),
            writtenNearbyAcceptedTexts: writtenNearbyTextsOf(bodyParagraphs, firstBookmarkedIndex, lastBookmarkedIndex, request.nearbyParagraphCount),
            /**
             * WHEN the audit asked for comments
             *   CONSEQUENCE
             *     the comments on the paragraph and on the one after it, where an "after_paragraph"
             *     comment is anchored.
             */
            commentTexts: request.readsComments ? [...bodyParagraphs[firstBookmarkedIndex].comments, ...(followingParagraphs[0]?.comments ?? [])].map((comment) => comment.text) : [],
            enclosingTable: request.readsTables ? enclosingTableOf(bodyParagraphs, firstBookmarkedIndex) : null,
            nearbyTablesCellTexts: request.readsTables
                ? nearbyTableNumbersOf(bodyParagraphs, firstBookmarkedIndex, lastBookmarkedIndex, followingParagraphs).map((tableNumber) => tableCellTextsOf(bodyParagraphs, tableNumber))
                : [],
        });
    }
    return facts;
}

/**
 * Reads the whole body once and answers every request from it: one Word round trip
 * whatever the number of cards, 0.12–0.33 s for 50 to 3,000 paragraphs (measured
 * 2026-10-14 for Apply All's whole-document read).
 *
 * WHY through the session's `readBody`
 *   Every whole-body read is published on the document's event stream, so the
 *   agents' views of the document take this read instead of reading again.
 */
export const readLandingPageFacts: LandingPageReader = (requests) => withWordContextRead(async (context) => {
    const bodyRead = await officeWordDocument.sessionFor(context).readBody();
    return landingPageFactsOfBody(bodyRead.flatOpc, requests);
});
